import { useMemo } from 'react';

import {
  CustomerConfirmStatusDetails,
  CustomerDataCollectionOrder,
  OrderStatus,
  OrderStatusView,
} from 'enums';
import { Order } from 'types';

interface UseOrderStatusViewInput {
  order: Order;
  status: OrderStatus;
  cardConfirmed: Boolean;
}

export const useOrderStatusView = ({
  order,
  status,
  cardConfirmed,
}: UseOrderStatusViewInput) => {
  const customerDataBeforePayment = useMemo(
    () =>
      order?.shop.customerDataCollectionOrder ===
      CustomerDataCollectionOrder.BeforePayment,
    [order?.shop.customerDataCollectionOrder],
  );

  const innerStatus = useMemo(() => {
    if (status === OrderStatus.CustomerConfirm) {
      return (customerDataBeforePayment && !cardConfirmed) ||
        (!customerDataBeforePayment &&
          order.statusDetails === CustomerConfirmStatusDetails.CustomerPayed)
        ? OrderStatusView.CustomerConfirmCard
        : OrderStatusView.CustomerConfirmTransfer;
    }
    return status as unknown as OrderStatusView;
  }, [status, customerDataBeforePayment, cardConfirmed, order.statusDetails]);

  const stepIndex = useMemo(
    () => ({
      [OrderStatusView.New]: 1,
      [OrderStatusView.Requisites]: 2,
      [OrderStatusView.TraderConfirm]: 4,
      [OrderStatusView.CustomerConfirm]: 3,
      ...(customerDataBeforePayment
        ? {
            [OrderStatusView.CustomerConfirmCard]: 3,
            [OrderStatusView.CustomerConfirmTransfer]: 4,
          }
        : {
            [OrderStatusView.CustomerConfirmCard]: 4,
            [OrderStatusView.CustomerConfirmTransfer]: 3,
          }),
      [OrderStatusView.Cancelled]: 5,
      [OrderStatusView.Completed]: 5,
      [OrderStatusView.Dispute]: 5,
      [OrderStatusView.Error]: 5,
    }),
    [customerDataBeforePayment],
  );

  return { innerStatus, stepIndex };
};
